import React, { useState, useEffect, ReactNode, forwardRef, useImperativeHandle } from "react";

interface CountdownContainerProps {
    initialSeconds?: number;
    waitText?: string;
    waitSeconds?: number;
    textPrefix?: string;
    textSuffix?: string;
    children?: ReactNode; // Accept children elements
}

const CountdownContainer = forwardRef<{}, CountdownContainerProps>((
    {
        initialSeconds = 60,
        waitSeconds = 3,
        waitText = '',
        textPrefix = "Control enabled in",
        textSuffix = "seconds",
        children,
    },
    ref
) => {
    const [seconds, setSeconds] = useState<number>(waitSeconds);
    const [isWaiting, setIsWaiting] = useState<boolean>(true); // State to track waiting period
    const [isEnabled, setIsEnabled] = useState<boolean>(false); // State to track if the countdown is enabled

    // Effect to handle the waiting period
    useEffect(() => {
        if (isWaiting) {
            const timer = setTimeout(() => {
                setIsWaiting(false); // Stop waiting after specified time
                setSeconds(initialSeconds); // Start countdown after wait time
            }, waitSeconds * 1000); // Convert seconds to milliseconds

            return () => clearTimeout(timer); // Clear timer on unmount
        }
    }, [isWaiting, waitSeconds, initialSeconds]);

    // Effect to handle the countdown logic
    useEffect(() => {
        if (seconds > 0) {
            const timer = setTimeout(() => setSeconds(seconds - 1), 1000);
            return () => clearTimeout(timer); // Clear timer on unmount
        } else {
            setIsEnabled(true); // Enable the link when countdown reaches zero
        }
    }, [seconds]);

    // Function to reset the countdown
    const resetCountdown = () => {
        setIsWaiting(true);
        setSeconds(waitSeconds); // Reset to waiting state
        setIsEnabled(false); // Disable children until waiting is complete
    };

    useImperativeHandle(ref, () => ({ resetCountdown, }));

    return (
        <div>
            {isWaiting ? (
                <p>{waitText}...{'(' + seconds + ')'}</p> // Show waiting message
            ) : seconds > 0 ? (
                <p>
                    {textPrefix} {seconds} {textSuffix} {/* Show countdown message */}
                </p>
            ) : (
                <>
                    {/* Only show the children when countdown is over */}
                    {isEnabled && children}
                    {/* {isEnabled && React.cloneElement(children as React.ReactElement, { onClick: resetCountdown })} */}

                </>
            )}
        </div>
    );
});

export default CountdownContainer;

/* sample usage

const countdownRef = useRef<{ resetCountdown: () => void }>(null); // Create a ref
if (countdownRef.current) {
    countdownRef.current.resetCountdown(); // Call the resetCountdown method
}

<CountdownContainer ref={countdownRef} initialSeconds={10} textPrefix="ไม่ได้รับรหัส? ส่งใหม่ใน" textSuffix="วินาที">
    <SpinnerLink caption="ส่ง OTP อีกครั้ง" captionProcess="กำลังส่ง..." onClick={onResendOtp}></SpinnerLink>
</CountdownContainer>
*/